import React, { useEffect, useState } from 'react';
import { fetchSingleSheet, ShowData, getShowPersonnel, hasShowPersonnel } from '../utils/googleSheets';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';

interface ShowsProps {
  csvMapping: Record<string, string>;
}

const Shows: React.FC<ShowsProps> = ({ csvMapping }) => {
  const [showsByYear, setShowsByYear] = useState<Record<string, ShowData[]>>({});
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({});
  const [expandedYears, setExpandedYears] = useState<Record<string, boolean>>({});
  const [expandedShows, setExpandedShows] = useState<Record<string, boolean>>({});

  const currentYear = new Date().getFullYear().toString();
  const sortedYears = Object.keys(csvMapping).sort((a, b) => b.localeCompare(a));

  useEffect(() => {
    // Initially load ONLY the current year
    loadYear(currentYear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadYear = async (year: string) => {
    if (showsByYear[year] || loadingStates[year]) return;

    setLoadingStates(prev => ({ ...prev, [year]: true }));
    try {
      const data = await fetchSingleSheet(csvMapping[year]);
      setShowsByYear(prev => ({ ...prev, [year]: data }));
    } finally {
      setLoadingStates(prev => ({ ...prev, [year]: false }));
    }
  };

  const toggleYear = async (year: string) => {
    const isExpanding = !expandedYears[year];
    setExpandedYears(prev => ({ ...prev, [year]: isExpanding }));
    
    if (isExpanding && !showsByYear[year]) {
      await loadYear(year);
    }
  };

  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const upcomingShows = (showsByYear[currentYear] || [])
    .filter(show => show.parsedDate >= now)
    .sort((a, b) => a.parsedDate.getTime() - b.parsedDate.getTime());

  const toggleShowExpansion = (showKey: string) => {
    setExpandedShows(prev => ({
      ...prev,
      [showKey]: !prev[showKey]
    }));
  };

  const renderPersonnel = (show: ShowData) => {
    const personnel = getShowPersonnel(show);
    const hasInfo = hasShowPersonnel(show);
    if (!hasInfo) return null;

    return (
      <div className="show-expanded-content" data-testid="show-expanded" onClick={(e) => e.stopPropagation()}>
        {personnel.length > 0 && (
          <div className="personnel-section">
            <div className="personnel-title">Band Lineup</div>
            <div className="personnel-grid">
              {personnel.map((member, i) => (
                <div key={i} className="personnel-member">
                  <span className="member-role">{member.role}</span>
                  <span className="member-name">{member.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {(show.event || show.notes) && (
          <div className="show-notes-section">
            {show.event && (
              <div className="show-note-item">
                <span className="note-label">Event</span>
                <span className="note-val">{show.event}</span>
              </div>
            )}
            {show.notes && (
              <div className="show-note-item">
                <span className="note-label">Notes</span>
                <span className="note-val">{show.notes}</span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderTable = (showList: ShowData[]) => {
    if (showList.length === 0) return <p style={{ color: 'var(--text-muted)', padding: '1rem' }}>No shows found for this period.</p>;
    return (
      <div>
        {/* Desktop View */}
        <div className="shows-desktop-view">
          <table className="shows-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Artist / Band</th>
                <th>Venue</th>
                <th>City</th>
              </tr>
            </thead>
            <tbody>
              {showList.map((show, index) => {
                const showKey = `${show.date}-${show.artistBand}-${show.venue}`;
                const isExpanded = !!expandedShows[showKey];
                const hasDetails = hasShowPersonnel(show);

                return (
                  <React.Fragment key={index}>
                    <tr 
                      className={`shows-table-row ${hasDetails ? 'clickable' : ''} ${isExpanded ? 'expanded' : ''}`}
                      data-testid="show-row"
                      onClick={() => hasDetails && toggleShowExpansion(showKey)}
                      style={{ cursor: hasDetails ? 'pointer' : 'default' }}
                    >
                      <td>{show.date}</td>
                      <td>{show.artistBand}</td>
                      <td>{show.venue}</td>
                      <td>{show.city}</td>
                    </tr>
                    {isExpanded && (
                      <tr className="shows-table-row-detail">
                        <td colSpan={4}>
                          {renderPersonnel(show)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile View */}
        <div className="shows-mobile-view">
          {showList.map((show, index) => {
            const showKey = `${show.date}-${show.artistBand}-${show.venue}`;
            const isExpanded = !!expandedShows[showKey];
            const hasDetails = hasShowPersonnel(show);

            return (
              <div 
                key={index} 
                className={`show-card ${hasDetails ? 'clickable' : ''} ${isExpanded ? 'expanded' : ''}`}
                data-testid="show-card"
                onClick={() => hasDetails && toggleShowExpansion(showKey)}
                style={{ cursor: hasDetails ? 'pointer' : 'default' }}
              >
                <div className="show-card-main">
                  <div className="show-card-date">{show.date}</div>
                  <div className="show-card-details">
                    <div className="show-card-artist">{show.artistBand}</div>
                    <div className="show-card-venue">{show.venue}</div>
                    {show.city && <div className="show-card-city">{show.city}</div>}
                  </div>
                </div>
                {isExpanded && renderPersonnel(show)}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="page-container">
      <h1 className="page-title">Live Shows</h1>
      
      <div className="shows-section">
        <h2>Upcoming Shows</h2>
        {loadingStates[currentYear] && !showsByYear[currentYear] ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-muted)' }}>
            <Loader2 className="animate-spin" size={20} /> Loading current dates...
          </div>
        ) : (
          upcomingShows.length > 0 ? renderTable(upcomingShows) : <p style={{ color: 'var(--text-muted)' }}>Check back soon for new dates.</p>
        )}
      </div>

      <div className="shows-section">
        <h2>Past Shows</h2>
        {sortedYears.map(year => (
          <div key={year} className="year-accordion" style={{ marginBottom: '1rem' }}>
            <button 
              onClick={() => toggleYear(year)}
              className="accordion-btn"
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '1.25rem 1.5rem',
                backgroundColor: 'var(--surface-color)',
                border: '1px solid var(--surface-border)',
                color: 'var(--text-main)',
                fontFamily: 'var(--font-mono)',
                fontSize: '1rem',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                cursor: 'pointer',
                borderRadius: '0.75rem',
                transition: 'all 0.2s ease'
              }}
            >
              <span>{year} Gigs</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                {loadingStates[year] && <Loader2 className="animate-spin" size={20} />}
                {expandedYears[year] ? <ChevronDown size={24} /> : <ChevronRight size={24} />}
              </div>
            </button>
            {expandedYears[year] && !loadingStates[year] && (
              <div style={{ padding: '1rem 0' }}>
                {renderTable((showsByYear[year] || []).filter(show => show.parsedDate < now))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Shows;
